import { atom } from 'nanostores';
import { FrameworkIcons } from '~/lib/ui/icons';

export type Framework =
  | 'react_tailwind'
  | 'react_chakra'
  | 'react_shadcn'
  | 'html_css'
  | 'html_tailwind'
  | 'angular_css'
  | 'vue_tailwind'
  | 'reactnative';

export type Language = 'ts' | 'js';

export type PackageManager = 'npm' | 'yarn' | 'pnpm' | 'bun';

export interface FrameworkConfig {
  label: string;
  type: 'web' | 'app';
  languages: readonly Language[];
  defaultLanguage: Language;
  icon: React.ReactNode;
  template: {
    org: string;
    repo: string;
  };
}

export const FRAMEWORK_CONFIGS: Record<Framework, FrameworkConfig> = {
  react_tailwind: {
    label: 'React + Tailwind',
    type: 'web',
    languages: ['ts', 'js'] as const,
    defaultLanguage: 'ts',
    icon: FrameworkIcons.react,
    template: {
      org: 'dualiteindia',
      repo: 'react-tailwind-template',
    },
  },
  react_chakra: {
    label: 'React + Chakra',
    type: 'web',
    languages: ['ts', 'js'] as const,
    defaultLanguage: 'ts',
    icon: FrameworkIcons.chakra,
    template: {
      org: 'dualiteindia',
      repo: 'react-chakra-template',
    },
  },
  react_shadcn: {
    label: 'React + Shadcn',
    type: 'web',
    languages: ['ts'] as const,
    defaultLanguage: 'ts',
    icon: FrameworkIcons.shadcn,
    template: {
      org: 'dualiteindia',
      repo: 'react-shadcn-template',
    },
  },
  html_css: {
    label: 'HTML + CSS',
    type: 'web',
    languages: ['js'] as const,
    defaultLanguage: 'js',
    icon: FrameworkIcons.html,
    template: {
      org: 'dualiteindia',
      repo: 'html-css-template',
    },
  },
  html_tailwind: {
    label: 'HTML + Tailwind',
    type: 'web',
    languages: ['js'] as const,
    defaultLanguage: 'js',
    icon: FrameworkIcons.tailwind,
    template: {
      org: 'dualiteindia',
      repo: 'html-tailwind-template',
    },
  },
  angular_css: {
    label: 'Angular + CSS',
    type: 'web',
    languages: ['ts'] as const,
    defaultLanguage: 'ts',
    icon: FrameworkIcons.angular,
    template: {
      org: 'dualiteindia',
      repo: 'angular-css-template',
    },
  },
  vue_tailwind: {
    label: 'Vue + Tailwind',
    type: 'web',
    languages: ['ts'] as const,
    defaultLanguage: 'ts',
    icon: FrameworkIcons.vue,
    template: {
      org: 'dualiteindia',
      repo: 'vue-tailwind-template',
    },
  },
  // nextjs_shadcn: {
  //   label: 'Next.js + Shadcn',
  //   type: 'web',
  //   languages: ['ts'] as const,
  //   defaultLanguage: 'ts',
  //   icon: FrameworkIcons.nextjs,
  //   template: {
  //     org: 'dualiteindia',
  //     repo: 'nextjs-shadcn-template',
  //   },
  // },
  reactnative: {
    label: 'React Native',
    type: 'app',
    languages: ['ts'] as const,
    defaultLanguage: 'ts',
    icon: FrameworkIcons.react,
    template: {
      org: 'dualiteindia',
      repo: 'react-native-template',
    },
  },
  // flutter: {
  //   label: 'Flutter',
  //   type: 'app',
  //   languages: ['dart'] as const,
  //   defaultLanguage: 'dart',
  //   icon: FrameworkIcons.react, // Placeholder icon
  //   template: {
  //     org: 'dualiteindia',
  //     repo: 'flutter-template',
  //   },
  // },
};

export interface TechStackSelectionConfig {
  framework: Framework;
  language: Language;
  packageManager: PackageManager;
}

const defaultConfig: TechStackSelectionConfig = {
  framework: 'react_tailwind',
  language: 'ts',
  packageManager: 'yarn',
};

export const techStackSelectionStore = atom<TechStackSelectionConfig>(defaultConfig);

export const setFramework = (framework: Framework) => {
  const config = FRAMEWORK_CONFIGS[framework];
  const currentLang = techStackSelectionStore.get().language;

  techStackSelectionStore.set({
    ...techStackSelectionStore.get(),
    framework,
    language: config.languages.includes(currentLang) ? currentLang : config.defaultLanguage,
  });
};

export const setLanguage = (language: Language) => {
  techStackSelectionStore.set({
    ...techStackSelectionStore.get(),
    language,
  });
};

export const isLanguageKey = (lang: string): lang is Language => {
  return ['ts', 'js', 'dart'].includes(lang);
};

export const setPackageManager = (packageManager: PackageManager) => {
  techStackSelectionStore.set({
    ...techStackSelectionStore.get(),
    packageManager,
  });
};

export const getPackageManager = (): PackageManager => {
  return techStackSelectionStore.get().packageManager;
};
